<script>
export default {
    page() {
        return { title: 'Your Artists' } // title only — noindex page
    },
}
</script>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useStore } from '@state/composables'
import Layout from '@layouts/main-layout'
import ArtistBlock from '@components/artist/artist-block'
import LibraryHeader from '@components/library/library-header'
import LibrarySearchResults from '@components/library/library-search-results'
import LibrarySectionTabs from '@components/library/library-section-tabs'
import libraryPreferences from '@services/library-preferences-service'
import { ARTIST_SORT_OPTIONS, sortArtists } from '@utils/library-collections'

const SECTION = 'artists'

const store = useStore()
const searchQuery = ref('')
const followedArtists = computed(() => store.getters['favorites/followedArtists'])
const artistsLoaded = computed(() => store.getters['favorites/isLoaded']('artists'))

// validate the persisted key — a stale/corrupt stored value would crash activeSortLabel's .find().label
const persistedSort = libraryPreferences.getSort(SECTION)
const sortKey = ref(
    ARTIST_SORT_OPTIONS.some(option => option.key === persistedSort) ? persistedSort : 'recentlyAdded'
)

const sortOptions = ARTIST_SORT_OPTIONS
const activeSortLabel = computed(() => sortOptions.find(option => option.key === sortKey.value).label)
const visibleArtists = computed(() => sortArtists(followedArtists.value, sortKey.value))

function handleSortChange(label) {
    const option = sortOptions.find(el => el.label === label)
    sortKey.value = option.key
    libraryPreferences.setSort(SECTION, option.key)
}

onMounted(() => {
    store.dispatch('favorites/fetchType', SECTION).catch(error => {
        // leave `loaded` untouched — the spinner (not a false empty state) is the FR21 failure mode
        console.error('Failed to refresh followed artists', error)
    })
})
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <LibraryHeader
                    title="Your Artists"
                    :count="artistsLoaded ? followedArtists.length : null"
                    count-label="artists"
                    :search-query="searchQuery"
                    @update:search-query="searchQuery = $event"
                />
                <LibrarySearchResults
                    v-if="searchQuery.trim()"
                    :query="searchQuery"
                />
                <template v-else>
                    <LibrarySectionTabs />
                    <div class="controls">
                        <BaseCustomSelect
                            class="sort"
                            prefix="Sort:"
                            :options="sortOptions.map(option => option.label)"
                            :active="activeSortLabel"
                            @change="handleSortChange"
                        />
                    </div>
                    <BaseSpinner v-if="!followedArtists.length && !artistsLoaded" />
                    <ArtistBlock
                        v-else-if="visibleArtists.length"
                        :artists="visibleArtists"
                    />
                    <BaseEmptyList
                        v-else
                        :error="{
                            title: 'No artists yet',
                            paragraph: 'Follow artists to keep their newest mixes one tap away.',
                        }"
                    />
                </template>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.controls {
    display: flex;
    flex-wrap: wrap;
    gap: $s-s;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $s-m;
}

.sort {
    margin-left: auto;
}
</style>
